/**
 * Take a user-uploaded share image (already cropped to 1200×630 by the
 * CropPicker) and bake the "Photobooth by Ofunc" branding overlay into it.
 * Output is what gets uploaded to S3 and used as og:image — so the brand
 * stays attached even when the image is downloaded and re-shared.
 */
export async function applyShareBranding(cropped: Blob): Promise<Blob> {
  const bm = await createImageBitmap(cropped);
  const w = bm.width;
  const h = bm.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bm.close();
    throw new Error('2D context unavailable');
  }
  ctx.drawImage(bm, 0, 0);
  bm.close();

  // Dimensions scale roughly with the image. Sizing keyed off the height
  // (since OG is fixed 1200×630, this is effectively a constant in
  // production — but it stays sensible if a non-standard target is used).
  const scale = h / 630;
  const margin = Math.round(28 * scale);
  const pillH = Math.round(54 * scale);
  const padX = Math.round(22 * scale);
  const fontPx = Math.round(22 * scale);

  // Soft dark gradient along the bottom so the pill stays readable on any
  // background image.
  const gradH = Math.round(220 * scale);
  const grad = ctx.createLinearGradient(0, h - gradH, 0, h);
  grad.addColorStop(0, 'rgba(0,0,0,0)');
  grad.addColorStop(1, 'rgba(0,0,0,0.55)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, h - gradH, w, gradH);

  ctx.font = `700 ${fontPx}px ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, system-ui, sans-serif`;
  const text = 'Photobooth by Ofunc';
  const textW = ctx.measureText(text).width;
  const pillW = Math.round(textW + padX * 2);

  const px = w - margin - pillW;
  const py = h - margin - pillH;

  // Pink pill — same brand color as the app.
  ctx.fillStyle = '#ff3a8c';
  roundedRect(ctx, px, py, pillW, pillH, pillH / 2);
  ctx.fill();

  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, px + pillW / 2, py + pillH / 2 + 1);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
      'image/jpeg',
      0.92,
    );
  });
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  // Some browsers ship CanvasRenderingContext2D.roundRect natively; if so
  // we prefer it because it handles edge cases. Otherwise fall back to a
  // manual arc path.
  const c = ctx as CanvasRenderingContext2D & {
    roundRect?: (x: number, y: number, w: number, h: number, r: number) => void;
  };
  if (typeof c.roundRect === 'function') {
    ctx.beginPath();
    c.roundRect(x, y, w, h, r);
    return;
  }
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
